import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { Button } from "../../components/ui/button";
import { formatDateTime } from "../../lib/format";
import { useForexAdmin } from "./api";
import {
  BASE,
  Fig,
  ListView,
  OpenedBy,
  Panel,
  QueryState,
  Stat,
  TabStrip,
  Unavail,
  UserLink,
  linkCls,
  orDash,
  useTab,
} from "./parts";
import {
  DAILY_COLUMNS,
  DEAL_COLUMNS,
  ORDER_HISTORY_COLUMNS,
  PNL_COLUMNS,
  TRADE_COLUMNS,
  dailyRow,
  dealRow,
  orderColumns,
  orderHistoryRow,
  orderRow,
  pnlRow,
  positionColumns,
  positionRow,
  tradeRow,
} from "./rows";

const RANGE = [
  { name: "from", label: "From", type: "datetime-local" },
  { name: "to", label: "To", type: "datetime-local" },
];
const SYMBOL = { name: "symbol", label: "Symbol (exact, e.g. XAUUSD)" };

const TABS = [
  { value: "positions", label: "Open positions" },
  { value: "orders", label: "Pending orders" },
  { value: "trades", label: "Closed trades" },
  { value: "deals", label: "Deals" },
  { value: "order-history", label: "Order history" },
  { value: "pnl", label: "P&L by symbol" },
  { value: "daily", label: "Daily" },
];

// `base` and `daily` let a deleted account (028) reuse the history tabs: its
// routes take the same queries under its login, and its daily range is known.
export function Tab({ id, tab, base, daily = { from: "", to: "" } }) {
  const a = base ?? `/accounts/admin/${id}`;
  switch (tab) {
    case "positions":
      return (
        <ListView path="/accounts/admin/positions" fixed={{ id }} empty={{}} fields={[]}
          columns={positionColumns(true)} row={positionRow(true)} label="positions" />
      );
    case "orders":
      return (
        <ListView path="/accounts/admin/orders" fixed={{ id }} empty={{}} fields={[]}
          columns={orderColumns(true)} row={orderRow(true)} label="orders" />
      );
    case "trades":
      return (
        <ListView path={`${a}/trades`} empty={{ symbol: "", from: "", to: "" }}
          fields={[SYMBOL, ...RANGE]} columns={TRADE_COLUMNS} row={tradeRow} label="trades" />
      );
    case "deals":
      return (
        <ListView
          path={`${a}/deals`}
          empty={{ action: "", reason: "", symbol: "", from: "", to: "" }}
          fields={[
            { name: "action", label: "Action", options: [
              { value: "", label: "All" }, { value: "TRADE", label: "Trades" }, { value: "BALANCE", label: "Balance operations" },
            ] },
            { name: "reason", label: "Placed by", options: [
              { value: "", label: "Anyone" }, { value: "BRIDGE", label: "Old bridge copies (Centroid)" },
            ] },
            SYMBOL,
            ...RANGE,
          ]}
          columns={DEAL_COLUMNS}
          row={dealRow}
          label="deals"
        />
      );
    case "order-history":
      return (
        <ListView
          path={`${a}/order-history`}
          empty={{ state: "" }}
          fields={[{ name: "state", label: "State", options: [
            { value: "", label: "All" }, { value: "FILLED", label: "Filled" },
            { value: "CANCELLED", label: "Cancelled" }, { value: "REJECTED", label: "Rejected" },
          ] }]}
          columns={ORDER_HISTORY_COLUMNS}
          row={orderHistoryRow}
          label="orders"
        />
      );
    case "pnl":
      return (
        <ListView
          path={`${a}/pnl`}
          paged={false}
          empty={{ from: "", to: "" }}
          fields={RANGE}
          columns={PNL_COLUMNS}
          row={pnlRow}
          rowsOf={(d) => d.symbols}
          head={(d) => (
            <div className="flex gap-3">
              <Stat label="Account floating now"><Fig v={d.floatingProfit} /></Stat>
              <Stat label="Range">
                {/* Either end may be open: only the other one was sent. */}
                <span className="text-sm">
                  {d.from || d.to
                    ? `${d.from ? formatDateTime(d.from) : "start"} → ${d.to ? formatDateTime(d.to) : "now"}`
                    : "All time"}
                </span>
              </Stat>
            </div>
          )}
        />
      );
    default:
      return (
        <ListView
          path={`${a}/daily`}
          paged={false}
          empty={daily}
          fields={[
            { name: "from", label: "From day", type: "date" },
            { name: "to", label: "To day", type: "date" },
          ]}
          columns={DAILY_COLUMNS}
          row={dailyRow}
          rowsOf={(d) => d.days}
          head={(d) => (
            <p className="text-xs text-muted-foreground">
              {d.from} → {d.to}. The trading server writes no row for today or weekends; a missing day is not zero.
            </p>
          )}
        />
      );
  }
}

// null = could not check (mirror down); false = drift.
const Match = ({ v }) =>
  v == null ? <Unavail /> : v ? <span className="text-emerald-400">Match</span> : <span className="text-red-400">Drift</span>;

const HOLD = {
  COPYING: "Held: copying a master. Stop copying first.",
  PENDING_SETTLEMENT: "Held: owes a master a share. Wait for the settlement.",
};

// Hold is worked out fresh and can touch the trading server, so it is asked
// for on demand rather than on every page load.
function Hold({ id }) {
  const [asked, setAsked] = useState(false);
  const q = useForexAdmin(asked ? `/accounts/admin/${id}/hold` : null);
  if (!asked)
    return (
      <Button size="sm" variant="outline" onClick={() => setAsked(true)}>
        Check withdrawal hold
      </Button>
    );
  if (q.error) return <ErrorBanner error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <span className="text-sm text-muted-foreground">Checking…</span>;
  return (
    <span className={`text-sm ${q.data.reason ? "text-amber-400" : "text-emerald-400"}`}>
      {q.data.reason ? HOLD[q.data.reason] ?? q.data.reason : "Not held: money can leave."}
      <span className="ml-2 font-mono text-xs text-muted-foreground">
        login {q.data.login} · id {q.data.id}
      </span>
    </span>
  );
}

export default function AccountDetail() {
  const { id } = useParams();
  const [tab, setTab] = useTab("positions");
  const detail = useForexAdmin(`/accounts/admin/${id}`);
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;
  const a = detail.data;

  return (
    <QueueLayout
      eyebrow="Forex Admin · Account"
      title={a ? `${a.login}` : "Account"}
      accent={a ? a.kind : null}
      description={a ? `${a.tier} · ${a.purpose} · ${a.group}` : null}
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      <Link to={`${BASE}/accounts`} className={`text-xs text-muted-foreground ${linkCls}`}>
        ← All accounts
      </Link>

      <QueryState q={detail}>
        {(a) => (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              <Stat label="Username"><UserLink username={a.username} /></Stat>
              <Stat label="Holder"><span className="text-sm">{a.holderName ?? <Unavail />}</span></Stat>
              <Stat label="Leverage">{a.leverage == null ? <Unavail /> : `1:${a.leverage}`}</Stat>
              <Stat label="Currency">{a.currency}</Stat>
              <Stat label="Group check"><Match v={a.groupMatches} /></Stat>
              <Stat label="Username check"><Match v={a.usernameMatches} /></Stat>
              <Stat label="Balance"><Fig v={a.balance} /></Stat>
              <Stat label="Credit"><Fig v={a.credit} /></Stat>
              <Stat label="Equity"><Fig v={a.equity} /></Stat>
              <Stat label="Margin"><Fig v={a.margin} /></Stat>
              <Stat label="Free margin"><Fig v={a.freeMargin} /></Stat>
              <Stat label="Margin level">{a.marginLevel == null ? orDash(null) : `${Number(a.marginLevel).toFixed(2)}%`}</Stat>
              <Stat label="Floating"><Fig v={a.floatingProfit} /></Stat>
              <Stat label="Realized"><Fig v={a.realizedProfit} /></Stat>
              <Stat label="Commission"><Fig v={a.commission} /></Stat>
              <Stat label="Swap"><Fig v={a.swap} /></Stat>
              <Stat label="Balance prev day"><Fig v={a.balancePrevDay} /></Stat>
              <Stat label="Equity prev day"><Fig v={a.equityPrevDay} /></Stat>
              <Stat label="Balance prev month"><Fig v={a.balancePrevMonth} /></Stat>
              <Stat label="Equity prev month"><Fig v={a.equityPrevMonth} /></Stat>
              <Stat label="Rights">{a.rights ?? <Unavail />}</Stat>
              <Stat label="Opened by"><span className="text-sm"><OpenedBy id={a.createdByOperatorId} /></span></Stat>
            </div>
            <div className="grid grid-cols-1 gap-1 text-xs text-muted-foreground sm:grid-cols-2">
              <span>Opened with Ginox: {formatDateTime(a.createdAt)}</span>
              <span>Registered on server: {formatDateTime(a.registeredAt)}</span>
              <span>Last access: {formatDateTime(a.lastAccessAt)} {a.lastAddress ? `from ${a.lastAddress}` : ""}</span>
              <span>
                <Link to={`${BASE}/transfers?login=${a.login}`} className={linkCls}>Transfers on this login →</Link>
              </span>
            </div>
          </div>
        )}
      </QueryState>

      <Panel title="Withdrawal hold">
        <Hold id={id} />
      </Panel>

      <div className="space-y-5">
        <TabStrip tabs={TABS} value={tab} onChange={setTab} />
        <Tab key={tab} id={id} tab={tab} />
      </div>
    </QueueLayout>
  );
}
